import { useBrainStore } from '@/state/brainStore';
import { aud } from '@/utils/format';
import { dayPayouts, dayExpectedPayouts, cashLedgerFor, type DayPayout } from '@/domain/payoutFlow';
import { depositChannel } from '@/domain/depositReconFlow';
import type { DepositChannel, MerchItem, SalePayment, SettlementLanding, StudioId } from '@/domain/types';
import { dayKey } from '@/utils/studioDay';
import { cardPortionAud, splitLump } from '@/domain/settlementFlow';

/**
 * Back-office reconciliation (Prompt E3). The verify step: a studio's day checked
 * off, system make-sale records (E1 `SalePayment`) matched against the terminal
 * dockets + deposit sheet by reference + amount, exceptions surfaced, and the
 * cash side closed against the E2 `CashLedger` (expected vs counted, variance).
 *
 * READ, DON'T RECOMPUTE: the per-artist calculation reads E2's `dayPayouts`
 * (envelope, sign-off state) straight from the store — it never re-runs the
 * commission maths its own way. This module only matches, aggregates and reports.
 */

/** Same studio (Melbourne) calendar day, whatever offset each ISO carries. */
function sameDay(aIso: string, bIso: string): boolean {
  return dayKey(aIso) === dayKey(bIso);
}

export type ReconSourceKind = 'card-sale' | 'cash-sale' | 'deposit-sheet';
export type ReconExceptionKind = 'missing-docket' | 'amount-mismatch' | 'name-missing';

export interface ReconException {
  kind: ReconExceptionKind;
  /** Human reason shown on the exceptions rail. */
  detail: string;
}

/** One reconciliation row: a system record (or deposit-sheet line) vs its echo. */
export interface ReconRow {
  id: string;
  reference: string;
  /** Client / artist label, as the office reads it. */
  name: string;
  kind: ReconSourceKind;
  /** Amount the system says. */
  systemAmountAud: number;
  /** Amount the docket / sheet echoed (absent when no echo landed). */
  docketAmountAud?: number;
  matched: boolean;
  /** Present only on an exception row. */
  exception?: ReconException;
  /** True when the sale was taken after the 17:00 cutoff — tomorrow's lump. */
  afterCutoff: boolean;
}

/** The cash-close side: expected in the till vs counted, with the variance. */
export interface CashClose {
  /** Physical cash expected in the till = float + cash-in − payouts handed out. */
  expectedAud: number;
  /** Cash counted at close (E4). Absent until the day has been counted. */
  countedAud?: number;
  counted: boolean;
  /** expected − counted (positive === till short, negative === till over). 0 when uncounted. */
  varianceAud: number;
  /** True once an emergency float dip was logged (flagged for the office). */
  floatDipped: boolean;
}

/**
 * The day's overall reconciliation state:
 *  - `balanced`      : no exceptions, the till has been counted, and it ties out.
 *  - `out-by`        : an exception to clear, or a counted till with a variance.
 *  - `pending-count` : the lines are clear but the till has not been counted yet
 *                      (counting happens at day close — E4). NOT the same as balanced.
 */
export type DayReconStatus = 'balanced' | 'out-by' | 'pending-count';

export interface DayReconciliation {
  studioId: StudioId;
  dayIso: string;
  rows: ReconRow[];
  matched: ReconRow[];
  exceptions: ReconRow[];
  /** Informational: after-cutoff sales that belong to tomorrow's lump. */
  afterCutoff: ReconRow[];
  cash: CashClose;
  status: DayReconStatus;
  /** Convenience: `status === 'balanced'`. Only true once the till is counted and ties out. */
  balanced: boolean;
  /** True when the day has been closed (E4) — this reconciliation reads the frozen pack. */
  closed: boolean;
}


/**
 * Reconcile a studio's day. Card/mixed sales are matched to terminal dockets by
 * reference + amount; cash sales reconcile on the cash side; deposit-sheet lines
 * are shown as their own rows. Exceptions are the three the office cares about:
 * a missing docket, an amount mismatch, and a docket missing a name.
 */
export function reconcileDay(studioId: StudioId, dayIso: string): DayReconciliation {
  const brain = useBrainStore.getState();

  // Once a day is closed (E4), the office reads the FROZEN pack, not live data,
  // so a later edit can't rewrite a settled day. A reopened pack falls back to live.
  const pack = brain.dayPacks[`${studioId}:${dayKey(dayIso)}`];
  const closed = pack !== undefined && !pack.reopenedAt;

  const sales = closed
    ? pack!.payments
    : Object.values(brain.salePayments).filter((s) => s.studioId === studioId && sameDay(s.takenAt, dayIso));
  const dockets = closed
    ? pack!.dockets
    : Object.values(brain.docketLines).filter((d) => d.studioId === studioId && sameDay(d.dayIso, dayIso));
  const terminalByRef = new Map(dockets.filter((d) => d.source === 'terminal').map((d) => [d.reference, d]));

  const rows: ReconRow[] = [];

  for (const sale of sales) {
    const name = sale.customerName;
    if (sale.method === 'cash') {
      // Cash never settles through the bank — it reconciles on the cash side.
      rows.push({
        id: sale.id,
        reference: sale.saleReference,
        name,
        kind: 'cash-sale',
        systemAmountAud: sale.cashInAud,
        matched: true,
        afterCutoff: sale.afterCutoff,
      });
      continue;
    }

    const charge = cardPortionAud(sale);
    const docket = terminalByRef.get(sale.saleReference);
    let exception: ReconException | undefined;
    if (sale.afterCutoff) {
      // Not an exception: the docket has not landed yet, it rides tomorrow's lump.
      exception = undefined;
    } else if (!docket) {
      exception = { kind: 'missing-docket', detail: `No docket echoed for ${sale.saleReference}.` };
    } else if (docket.amountAud !== charge) {
      exception = {
        kind: 'amount-mismatch',
        detail: `System ${aud(charge)} vs docket ${aud(docket.amountAud)}.`,
      };
    } else if (!docket.name || docket.name.trim() === '') {
      exception = { kind: 'name-missing', detail: `Docket ${sale.saleReference} has no name written on it.` };
    }

    rows.push({
      id: sale.id,
      reference: sale.saleReference,
      name,
      kind: 'card-sale',
      systemAmountAud: charge,
      docketAmountAud: docket?.amountAud,
      matched: !sale.afterCutoff && !exception,
      exception,
      afterCutoff: sale.afterCutoff,
    });
  }

  // Deposit-sheet lines stand as their own rows (the deposits taken toward the
  // day's pieces). A sheet line with no name written is the same name-missing
  // exception the office chases.
  for (const line of dockets.filter((d) => d.source === 'deposit-sheet')) {
    const nameMissing = !line.name || line.name.trim() === '';
    rows.push({
      id: line.id,
      reference: line.reference,
      name: line.name ?? '(no name)',
      kind: 'deposit-sheet',
      systemAmountAud: line.amountAud,
      docketAmountAud: line.amountAud,
      matched: !nameMissing,
      exception: nameMissing
        ? { kind: 'name-missing', detail: `Deposit sheet line ${line.reference} has no name.` }
        : undefined,
      afterCutoff: false,
    });
  }

  const ledger = closed ? pack!.cashLedger : cashLedgerFor(studioId, dayIso);
  const expectedAud = ledger.openingFloatAud + ledger.cashInAud - ledger.payoutsAud;
  const counted = ledger.countedAud !== undefined;
  const varianceAud = counted ? expectedAud - (ledger.countedAud ?? 0) : 0;
  const cash: CashClose = {
    expectedAud,
    countedAud: ledger.countedAud,
    counted,
    varianceAud,
    floatDipped: ledger.floatDipped,
  };

  const exceptions = rows.filter((r) => r.exception);
  const afterCutoff = rows.filter((r) => r.afterCutoff);
  const matched = rows.filter((r) => r.matched && !r.afterCutoff);

  // An exception, or a counted till that does not tie out, is "out by". With no
  // exceptions but the till not yet counted, the day is "pending count" (the
  // count happens at day close, E4) — deliberately NOT reported as balanced.
  const cashOut = counted && varianceAud !== 0;
  const status: DayReconStatus =
    exceptions.length > 0 || cashOut ? 'out-by' : counted ? 'balanced' : 'pending-count';

  return {
    studioId,
    dayIso: dayKey(dayIso),
    rows,
    matched,
    exceptions,
    afterCutoff,
    cash,
    status,
    balanced: status === 'balanced',
    closed,
  };
}

// --- Daily calculations screen -------------------------------------------

/** One artist's line on the calculations screen (all read from E2, not recomputed). */
export interface CalcArtistRow {
  artistId: string;
  artistName: string;
  sessionCount: number;
  /** Commissionable gross (day rates; merch excluded). */
  grossAud: number;
  /** The artist's envelope (their commission for the day). */
  payoutAud: number;
  /** The studio's share of this artist's gross. */
  houseAud: number;
  /** Sign-off state from the E2 payout record. */
  signState: 'signed' | 'deferred' | 'due' | 'in-chair';
  /**
   * When this artist's envelope falls due — their last committed session of the
   * day, clamped to the 6pm clock-off. An artist is paid at their OWN finish,
   * not at the end of the day, so the money is time-phased and the row has to
   * carry the moment.
   *
   * Read from the COMMITTED row, never from the row's own counted sessions: the
   * latter is only the end of the work in this table, which mid-day reads as an
   * earlier (and wrong) clock-off for an artist who still has clients to come.
   */
  finishIso?: string;
}

export interface DayCalculation {
  studioId: StudioId;
  dayIso: string;
  rows: CalcArtistRow[];
  /** Session turnover (commissionable gross across all artists). */
  turnoverAud: number;
  /** Total paid out to artists (sum of envelopes). */
  artistsAud: number;
  /** The studio's share of sessions = turnover − artists. */
  oursAud: number;
  /** Merch/aftercare sold that day — earned revenue, no commission (all house). */
  merchAud: number;
}

function signStateFor(p: DayPayout): CalcArtistRow['signState'] {
  if (p.record?.status === 'signed') return 'signed';
  if (p.record?.status === 'deferred') return 'deferred';
  if (!p.payable) return 'in-chair';
  return 'due';
}

/**
 * The day's money for one studio, per artist. Reads E2's `dayPayouts` directly so
 * the envelope + sign-off state can never disagree with the payouts screen.
 */
export function dayCalculation(studioId: StudioId, dayIso: string): DayCalculation {
  const brain = useBrainStore.getState();
  const payouts = dayPayouts(studioId, dayIso);
  // Clock-off per artist, off the COMMITTED set — see CalcArtistRow.finishIso.
  const finishByArtist = new Map(
    dayExpectedPayouts(studioId, dayIso).map((p) => [p.artistId, p.expectedFinishIso]),
  );
  const rows: CalcArtistRow[] = payouts.map((p) => ({
    artistId: p.artistId,
    artistName: p.artistName,
    sessionCount: p.sessionCount,
    grossAud: p.takingsAud,
    payoutAud: p.envelopeAud,
    houseAud: Math.max(0, p.takingsAud - p.envelopeAud),
    signState: signStateFor(p),
    finishIso: finishByArtist.get(p.artistId) ?? p.expectedFinishIso,
  }));

  const turnoverAud = rows.reduce((sum, r) => sum + r.grossAud, 0);
  const artistsAud = rows.reduce((sum, r) => sum + r.payoutAud, 0);
  const merchAud = Object.values(brain.salePayments)
    .filter((s) => s.studioId === studioId && sameDay(s.takenAt, dayIso))
    .reduce((sum, s) => sum + s.merchAud, 0);
  return {
    studioId,
    dayIso: dayKey(dayIso),
    rows,
    turnoverAud,
    artistsAud,
    oursAud: turnoverAud - artistsAud,
    merchAud,
  };
}

/**
 * New money in via deposits on a day (money-in view, NOT revenue — a deposit is a
 * liability until the job is done). Counts actual money movements only: ledger
 * `paid` + `topup` entries dated that day, or a ledger-less paid deposit's paid
 * amount on its paid date. Excludes checkout rollovers (`dep-roll-*` / `rollover-in`)
 * — those move existing credit forward, they are not fresh money. Channel- and
 * surface-agnostic: reads every `Deposit` however it was taken.
 */
/** One line of the deposits-received breakdown: a deposit's money-in for the day. */
export interface DepositReceivedRow {
  id: string;
  name: string;
  channel: DepositChannel;
  amountAud: number;
}

/**
 * Per-deposit breakdown of the money that came in via deposits on a day — the same
 * money-movement rule as `depositsReceived`, one row each (customer, channel, amount),
 * so the report can show what made up the total. Excludes rollovers.
 */
export function depositsReceivedBreakdown(studioId: StudioId, dayIso: string): DepositReceivedRow[] {
  const brain = useBrainStore.getState();
  const rows: DepositReceivedRow[] = [];
  for (const d of Object.values(brain.deposits)) {
    if (d.studioId !== studioId) continue;
    if (d.id.startsWith('dep-roll-')) continue; // checkout rollover — not new money
    let amountAud = 0;
    if (d.ledger && d.ledger.length > 0) {
      for (const e of d.ledger) {
        if ((e.kind === 'paid' || e.kind === 'topup') && sameDay(e.at, dayIso)) amountAud += e.amountAud;
      }
    } else if (d.status === 'paid' && d.paidAt && sameDay(d.paidAt, dayIso)) {
      amountAud = d.amountPaid ?? d.amountAud;
    }
    if (amountAud <= 0) continue;
    const c = brain.customers[d.customerId];
    rows.push({
      id: d.id,
      name: c ? `${c.firstName} ${c.lastName}` : (d.depositReference ?? '(deposit)'),
      channel: depositChannel(d),
      amountAud,
    });
  }
  return rows.sort((a, b) => b.amountAud - a.amountAud);
}

/** New money in via deposits on a day (money-in view, NOT revenue). Sum of the breakdown. */
export function depositsReceived(studioId: StudioId, dayIso: string): number {
  return depositsReceivedBreakdown(studioId, dayIso).reduce((sum, r) => sum + r.amountAud, 0);
}

/** One line of the merch breakdown: an item sold that day, aggregated. */
export interface MerchBreakdownRow {
  itemId: string;
  name: string;
  category: MerchItem['category'] | 'other';
  qty: number;
  totalAud: number;
}

/**
 * Per-item merch/aftercare breakdown for a day — aggregates the `merchLines` across
 * the day's sales (one line per item sold), so the report can show what the merch
 * total was made of. Category comes from the studio catalogue.
 */
export function merchBreakdown(studioId: StudioId, dayIso: string): MerchBreakdownRow[] {
  const brain = useBrainStore.getState();
  const byItem = new Map<string, MerchBreakdownRow>();
  for (const s of Object.values(brain.salePayments)) {
    if (s.studioId !== studioId || !sameDay(s.takenAt, dayIso)) continue;
    for (const line of s.merchLines) {
      const existing = byItem.get(line.itemId);
      if (existing) {
        existing.qty += 1;
        existing.totalAud += line.priceAud;
      } else {
        byItem.set(line.itemId, {
          itemId: line.itemId,
          name: line.name,
          category: brain.merchItems[line.itemId]?.category ?? 'other',
          qty: 1,
          totalAud: line.priceAud,
        });
      }
    }
  }
  return [...byItem.values()].sort((a, b) => b.totalAud - a.totalAud);
}

/** A running weekly tally across the seven days ending on (and including) `endDayIso`. */
export interface WeeklyTally {
  turnoverAud: number;
  artistsAud: number;
  oursAud: number;
  dayCount: number;
  /** Merch/aftercare sold across the week (earned, all house). */
  merchAud: number;
  /** New money in via deposits across the week (money-in, not revenue). */
  depositsReceivedAud: number;
}

/** yyyy-MM-dd strings for the 7-day window ending on (and including) `endDayIso`.
 *  Anchored at noon UTC so day arithmetic stays on calendar dates regardless of
 *  the machine timezone (the seeds carry a +10:00 offset). */
export function weekDays(endDayIso: string): string[] {
  const end = new Date(`${endDayIso.slice(0, 10)}T12:00:00Z`);
  const days: string[] = [];
  for (let i = 6; i >= 0; i -= 1) {
    const d = new Date(end.getTime() - i * 86_400_000);
    days.push(d.toISOString().slice(0, 10));
  }
  return days;
}

/** Sum the day calculations across the trading week ending on `endDayIso`. */
export function weeklyTally(studioId: StudioId, endDayIso: string): WeeklyTally {
  const days = weekDays(endDayIso);
  let turnoverAud = 0;
  let artistsAud = 0;
  let dayCount = 0;
  let merchAud = 0;
  let depositsReceivedAud = 0;
  for (const day of days) {
    const calc = dayCalculation(studioId, day);
    // Merch + deposits can land on a day with no commissionable session, so tally
    // them across every day, not only trading days.
    merchAud += calc.merchAud;
    depositsReceivedAud += depositsReceived(studioId, day);
    if (calc.rows.length === 0) continue;
    turnoverAud += calc.turnoverAud;
    artistsAud += calc.artistsAud;
    dayCount += 1;
  }
  return { turnoverAud, artistsAud, oursAud: turnoverAud - artistsAud, dayCount, merchAud, depositsReceivedAud };
}

// --- Cash-timing strip (settlement landings) -----------------------------

/** One column of the 7-day cash-timing strip. */
export interface TimingStripDay {
  dayIso: string;
  /** Card takings taken that trading day (money earned). */
  takingsAud: number;
  /** Settlement lumps that land in the bank that day (money arriving). */
  landingAud: number;
  /** True on the Fri-Tue window where nothing settles (the weekend gap). */
  weekendGap: boolean;
}

/** Weekday for a yyyy-MM-dd, timezone-independent (noon UTC avoids offset rollover). */
export function weekdayOf(dayIso: string): number {
  return new Date(`${dayIso.slice(0, 10)}T12:00:00Z`).getUTCDay(); // 0 = Sun … 6 = Sat
}

/**
 * The 7-day cash-timing strip for a studio, ending on `endDayIso`. A display over
 * seeded `SettlementLanding` data (no new settlement logic here): per day, the
 * card takings taken that day versus the lumps landing in the bank that day. The
 * bank does not settle on weekends, so Friday and Saturday takings visibly pile
 * into Monday/Tuesday landings — the locked weekend gap.
 */
export function cashTimingStrip(studioId: StudioId, endDayIso: string): TimingStripDay[] {
  const brain = useBrainStore.getState();
  const landings = Object.values(brain.settlementLandings).filter((l) => l.studioId === studioId);
  const takingsByDay = new Map<string, number>();
  const landingByDay = new Map<string, number>();
  for (const l of landings) {
    add(takingsByDay, dayKey(l.paymentDayIso), l.amountAud);
    add(landingByDay, dayKey(l.landedOnIso), l.amountAud);
  }

  return weekDays(endDayIso).map((dayIso) => {
    const dow = weekdayOf(dayIso);
    return {
      dayIso,
      takingsAud: takingsByDay.get(dayIso) ?? 0,
      landingAud: landingByDay.get(dayIso) ?? 0,
      // Saturday / Sunday: the bank is shut, nothing settles.
      weekendGap: dow === 0 || dow === 6,
    };
  });
}

function add(map: Map<string, number>, key: string, amount: number): void {
  map.set(key, (map.get(key) ?? 0) + amount);
}

/** All settlement landings for a studio, newest landing first (E4 will build on this). */
export function settlementLandingsFor(studioId: StudioId): SettlementLanding[] {
  return Object.values(useBrainStore.getState().settlementLandings)
    .filter((l) => l.studioId === studioId)
    .sort((a, b) => b.landedOnIso.localeCompare(a.landedOnIso));
}

/** yyyy-MM-dd `days` away from `dayIso` (noon-UTC anchored, tz-independent). */
export function shiftDayIso(dayIso: string, days: number): string {
  const d = new Date(`${dayIso.slice(0, 10)}T12:00:00Z`);
  return new Date(d.getTime() + days * 86_400_000).toISOString().slice(0, 10);
}

/**
 * The distinct business days a studio has reconciliation data for (any make-sale,
 * docket or cash ledger), newest first — the day picker on `/reconciliation`.
 */
export function reconciliationDaysFor(studioId: StudioId): string[] {
  const brain = useBrainStore.getState();
  const days = new Set<string>();
  for (const s of Object.values(brain.salePayments)) {
    if (s.studioId === studioId) days.add(dayKey(s.takenAt));
  }
  for (const d of Object.values(brain.docketLines)) {
    if (d.studioId === studioId) days.add(dayKey(d.dayIso));
  }
  for (const l of Object.values(brain.cashLedgers)) {
    if (l.studioId === studioId) days.add(dayKey(l.dayIso));
  }
  return [...days].sort((a, b) => b.localeCompare(a));
}

// --- Make-sale lane (amendment 11 — reconcile + settlement unified) --------

/** Hours a card sale can sit with no landed lump before it is flagged overdue. */
const BANK_OVERDUE_HOURS = 72;

/** Where a sale's money is: cash in the till, or landed / awaiting / overdue at the bank. */
export type BankStage = 'till' | 'landed' | 'awaiting' | 'overdue';

/** The bank lump a card sale's money landed in (or is due to land in). */
export interface SaleSettlement {
  /** Settlement-lump id — the checkable bank-statement reference. */
  batchRef: string;
  /** Neutral terminal / link the echo came through (never a card-provider brand). */
  terminal: string;
  /** This sale's split of the lump. */
  amountAud: number;
  /** ISO date the lump landed (or is due to land). */
  landedOn: string;
}

/**
 * One make-sale row: the money that actually moved this day and its
 * recorded → EFTPOS-echoed → bank-landed trail. Reconciliation is about real
 * money, so the deposit (collected on its own earlier day) is deliberately not
 * carried here — it reconciles in the deposit lane on the day it was paid.
 */
export interface MakeSaleRow {
  id: string;
  /** Trading day the sale was taken. */
  dayIso: string;
  reference: string;
  name: string;
  method: SalePayment['method'];
  cashAud: number;
  cardAud: number;
  /** cash + card that actually moved this day (deposit excluded — it reconciles on its paid day). */
  movedAud: number;
  /** What the EFTPOS terminal echoed back (card only; undefined for cash / no docket). */
  docketAud?: number;
  /** The bank lump this sale's card money landed in / is due in (undefined when none carries the reference). */
  settlement?: SaleSettlement;
  bankStage: BankStage;
  /** Same-day terminal-docket exception, when there is one. */
  exception?: ReconException;
  afterCutoff: boolean;
}

export interface MakeSaleLaneResult {
  rows: MakeSaleRow[];
  /** Docket exceptions + overdue bank lines — the only rows that need chasing. */
  needsYou: MakeSaleRow[];
  matched: MakeSaleRow[];
  awaitingBank: MakeSaleRow[];
}

/**
 * The bank lump carrying a card sale's money, matched by the stamped sale
 * reference — the split the office ticks against the bank statement. Undefined for
 * a cash sale (reconciles in the till) or when no lump has echoed the reference
 * yet. Provider-agnostic: never names a card brand.
 */
export function saleSettlement(sale: SalePayment): SaleSettlement | undefined {
  if (sale.method === 'cash') return undefined;
  const brain = useBrainStore.getState();
  for (const lump of Object.values(brain.settlementLumps)) {
    if (lump.studioId !== sale.studioId) continue;
    const line = lump.lines.find((l) => l.reference === sale.saleReference);
    if (!line) continue;
    return { batchRef: lump.id, terminal: line.terminal, amountAud: line.amountAud, landedOn: lump.landedOn };
  }
  return undefined;
}

/** Bank stage derived from a sale's already-resolved settlement, so stage and detail agree. */
function bankStageOf(sale: SalePayment, settlement: SaleSettlement | undefined, nowIso: string): BankStage {
  if (sale.method === 'cash') return 'till';
  const now = new Date(nowIso).getTime();
  // Landed from the start of the (studio-local) landing day — a date-only landedOn
  // parsed as an instant is UTC midnight, 10am in Melbourne.
  if (settlement) return dayKey(settlement.landedOn) <= dayKey(nowIso) ? 'landed' : 'awaiting';
  const ageHours = (now - new Date(sale.takenAt).getTime()) / 3_600_000;
  return ageHours > BANK_OVERDUE_HOURS ? 'overdue' : 'awaiting';
}

/**
 * Where a card/mixed sale's money sits at the bank. A cash sale reconciles in the
 * till. Otherwise: landed once its lump's day has arrived; awaiting while its lump
 * is still to land (or it is recent with none yet); overdue when nothing has echoed
 * past the window. Reads the same lump lookup as `saleSettlement` so the stage and
 * the settlement detail can never disagree.
 */
export function saleBankStage(sale: SalePayment, nowIso: string): BankStage {
  return bankStageOf(sale, saleSettlement(sale), nowIso);
}

function saleBreakdown(sale: SalePayment): Pick<MakeSaleRow, 'cashAud' | 'cardAud' | 'movedAud'> {
  const cashAud = sale.cashInAud;
  const cardAud = cardPortionAud(sale);
  return { cashAud, cardAud, movedAud: cashAud + cardAud };
}

/**
 * The make-sale reconciliation lane across every day the studio has data for (the
 * UI day filter scopes it). Reuses `reconcileDay`'s docket matching, drops the
 * deposit-sheet rows (deposits have their own lane now), and folds settlement in as
 * a per-line bank stage. Exceptions-first: a docket exception or an overdue bank
 * line needs you; a not-yet-landed line is awaiting; the rest are matched.
 */
export function makeSaleLane(studioId: StudioId, nowIso: string): MakeSaleLaneResult {
  const brain = useBrainStore.getState();
  const rows: MakeSaleRow[] = [];
  for (const day of reconciliationDaysFor(studioId)) {
    for (const r of reconcileDay(studioId, day).rows) {
      if (r.kind === 'deposit-sheet') continue;
      const sale = brain.salePayments[r.id];
      if (!sale) continue;
      const settlement = saleSettlement(sale);
      rows.push({
        id: r.id,
        dayIso: dayKey(sale.takenAt),
        reference: r.reference,
        name: r.name,
        method: sale.method,
        ...saleBreakdown(sale),
        docketAud: r.docketAmountAud,
        settlement,
        bankStage: bankStageOf(sale, settlement, nowIso),
        exception: r.exception,
        afterCutoff: r.afterCutoff,
      });
    }
  }
  rows.sort((a, b) => b.dayIso.localeCompare(a.dayIso) || a.reference.localeCompare(b.reference));

  return {
    rows,
    needsYou: rows.filter((r) => r.exception || r.bankStage === 'overdue'),
    matched: rows.filter((r) => !r.exception && (r.bankStage === 'landed' || r.bankStage === 'till')),
    awaitingBank: rows.filter((r) => !r.exception && r.bankStage === 'awaiting'),
  };
}

// --- Money with no sale (W7 n2) --------------------------------------------

/**
 * Where the unexplained money showed up:
 *  - `bank-line`       : a settlement line whose reference matches no sale;
 *  - `bank-mismatch`   : a settlement line whose reference matches a sale, but
 *                        whose amount differs from the sale's card portion;
 *  - `terminal-docket` : a terminal docket with no sale behind it.
 */
export type OrphanSource = 'bank-line' | 'bank-mismatch' | 'terminal-docket';

/** One "money with no sale" row. Resolves on the make-sale lane by its `id`. */
export interface OrphanRow {
  /** `orphan-bank:<lumpId>:<ref>` or `orphan-docket:<docketId>` — the resolution key. */
  id: string;
  /** The day the money was taken (the docket's day, or the line's paid-at day). */
  dayIso: string;
  reference: string;
  /** Resolve/approve rows show a name; an orphan has no client, so it names its source. */
  name: string;
  source: OrphanSource;
  /** Neutral terminal id, when the echo carried one (bank lines do; dockets don't). */
  terminal?: string;
  /** What arrived, at the bank or on the terminal. */
  amountAud: number;
  /** The matching sale's card portion, on a `bank-mismatch`. */
  saleAmountAud?: number;
  /** The bank lump carrying it, when it has landed in one. */
  batchRef?: string;
}

const ORPHAN_NAME: Record<OrphanSource, string> = {
  'bank-line': 'Bank line · no sale',
  'bank-mismatch': 'Bank ≠ sale',
  'terminal-docket': 'Terminal docket · no sale',
};

/**
 * Money that arrived with no sale to explain it: the three cases above, across
 * every day the studio has data for (the UI day filter scopes it). Reuses
 * `splitLump`'s reference + amount match for the bank side. A terminal docket
 * with no sale that the bank later settled shows ONCE (as the docket, carrying
 * its batch), never as two rows for the same money. Read-only; it only lists.
 */
export function orphanLane(studioId: StudioId): OrphanRow[] {
  const brain = useBrainStore.getState();
  const sales = Object.values(brain.salePayments).filter((s) => s.studioId === studioId);
  const saleRefs = new Set(sales.map((s) => s.saleReference));
  const saleByRef = new Map(sales.map((s) => [s.saleReference, s]));
  const lumps = Object.values(brain.settlementLumps).filter((l) => l.studioId === studioId);
  const batchOf = (ref: string) => lumps.find((l) => l.lines.some((x) => x.reference === ref));

  const rows: OrphanRow[] = [];
  const orphanDocketRefs = new Set<string>();
  for (const d of Object.values(brain.docketLines)) {
    if (d.studioId !== studioId || d.source !== 'terminal' || saleRefs.has(d.reference)) continue;
    orphanDocketRefs.add(d.reference);
    const lump = batchOf(d.reference);
    rows.push({
      id: `orphan-docket:${d.id}`,
      dayIso: dayKey(d.dayIso),
      reference: d.reference,
      name: ORPHAN_NAME['terminal-docket'],
      source: 'terminal-docket',
      amountAud: d.amountAud,
      ...(lump ? { batchRef: lump.id } : {}),
    });
  }

  for (const lump of lumps) {
    for (const line of splitLump(lump, sales).orphans) {
      if (orphanDocketRefs.has(line.reference)) continue; // already listed as its docket
      const sale = saleByRef.get(line.reference);
      const source: OrphanSource = sale ? 'bank-mismatch' : 'bank-line';
      rows.push({
        id: `orphan-bank:${lump.id}:${line.reference}`,
        dayIso: dayKey(line.paidAt),
        reference: line.reference,
        name: ORPHAN_NAME[source],
        source,
        terminal: line.terminal,
        amountAud: line.amountAud,
        ...(sale ? { saleAmountAud: cardPortionAud(sale) } : {}),
        batchRef: lump.id,
      });
    }
  }
  return rows.sort((a, b) => b.dayIso.localeCompare(a.dayIso) || a.reference.localeCompare(b.reference));
}
